import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Mail } from 'lucide-react';
import {
  PageLayout,
  Button,
  FormField,
  DetailCard,
  DetailRow,
} from '../common/index.js';

const EditContact = () => {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContact();
  }, [contactId]);

  const fetchContact = async () => {
    try {
      const response = await contactService.getContact(contactId);
      setContact(response.data.data);
      setReply(response.data.data.reply || '');
    } catch (err) {
      Alert.error('Failed to load contact data');
      console.error(err);
      navigate('/contact');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reply.trim()) {
      Alert.error('Please enter a reply message');
      return;
    }

    setSubmitting(true);

    try {
      await contactService.sendReply(contactId, { reply });
      Alert.success('Reply sent successfully!');
      setTimeout(() => navigate('/contact'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!contact?.reply && reply.trim() !== '') {
      const confirmed = await Alert.confirm(
        'You have unsaved changes. Are you sure you want to leave?',
        'Unsaved Changes'
      );
      if (!confirmed) return;
    }
    navigate('/contact');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <PageLayout title="Reply to Contact" icon={Mail}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading contact data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const isAlreadyReplied = contact?.reply;

  return (
    <PageLayout
      title={isAlreadyReplied ? 'View Reply' : 'Reply to Contact'}
      icon={Mail}
      actions={
        <Button variant="outline" onClick={() => navigate('/contact')}>
          Back to Contacts
        </Button>
      }
    >
      {isAlreadyReplied && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-card p-4 mb-4">
          <h3 className="text-emerald-900 font-semibold">Already Replied</h3>
          <p className="text-emerald-700 text-sm mt-1">
            This contact has already been replied to on {formatDate(contact.replied_at)}.
          </p>
        </div>
      )}

      <div className="space-y-4 mb-6">
        <DetailCard title="Contact Information">
          <DetailRow label="Full Name" value={contact.full_name} />
          <DetailRow label="Email" value={contact.email} />
          <DetailRow label="Phone" value={contact.phone} />
          <DetailRow label="Location" value={contact.location || 'N/A'} />
        </DetailCard>

        <DetailCard title="Message Details">
          <DetailRow label="Subject" value={contact.subject} />
          <DetailRow
            label="Message"
            value={<p className="whitespace-pre-wrap">{contact.message}</p>}
          />
          <DetailRow label="Date Received" value={formatDate(contact.created_at)} />
        </DetailCard>
      </div>

      {isAlreadyReplied ? (
        <DetailCard title="Reply Sent">
          <DetailRow
            label="Reply Message"
            value={<p className="whitespace-pre-wrap">{contact.reply}</p>}
          />
          <DetailRow label="Replied By" value={contact.replied_by_name || 'N/A'} />
          <DetailRow label="Replied At" value={formatDate(contact.replied_at)} />
        </DetailCard>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-card p-4 sm:p-6 space-y-4">
          <h3 className="text-base font-semibold text-secondary">Send Reply</h3>
          <FormField label="Reply Message" required>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Enter your reply message here..."
              required
              rows={6}
              className="input-clean w-full resize-none"
            />
            <p className="text-xs text-gray-600 mt-2">{reply.length} characters</p>
          </FormField>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} className="sm:ml-auto">
              {submitting ? 'Sending...' : 'Send Reply'}
            </Button>
          </div>
        </form>
      )}
    </PageLayout>
  );
};

export default EditContact;
